/**
 * Runtime-Public-Env fuer die Janeway-Web-Apps.
 *
 * Problem: Next.js inlined `process.env.NEXT_PUBLIC_*` zur BUILD-Zeit fest ins
 * Bundle (Client UND Server). Dadurch sind die Web-App-Images install-spezifisch
 * (Domain/Supabase-URL eingebacken) und nicht generisch pull-bar.
 *
 * Loesung: client-seitige Werte werden zur LAUFZEIT bereitgestellt:
 *  - Client liest `window.__JANEWAY_ENV` (injiziert via <PublicEnvScript/> aus
 *    `@janeway/gui-elements`, das im Root-Layout steht; nutzt die hier
 *    exportierten PUBLIC_ENV_KEYS).
 *  - Server liest `process.env[key]` DYNAMISCH — dynamische Indizierung wird von
 *    Next NICHT zur Build-Zeit inlined -> echter Laufzeit-Read aus dem Container-Env.
 *
 * Effekt: keine NEXT_PUBLIC-Werte mehr im Bundle gebacken -> generische Images,
 * Werte kommen pro Install aus `.env` (Compose -> Server -> Injection).
 */

export const PUBLIC_ENV_KEYS = [
  "NEXT_PUBLIC_SUPABASE_URL",
  "NEXT_PUBLIC_SUPABASE_ANON_KEY",
  "NEXT_PUBLIC_COOKIE_DOMAIN",
  "NEXT_PUBLIC_AI_URL",
  "NEXT_PUBLIC_CONTACTS_URL",
  "NEXT_PUBLIC_TASKS_URL",
  "NEXT_PUBLIC_CALENDAR_URL",
  "NEXT_PUBLIC_NOTES_URL",
  "NEXT_PUBLIC_IMMO_URL",
  "NEXT_PUBLIC_AGENTS_URL",
  "NEXT_PUBLIC_ADMIN_URL",
  "NEXT_PUBLIC_AUTH_URL",
  "NEXT_PUBLIC_STATUS_URL",
  "NEXT_PUBLIC_API_URL",
  "NEXT_PUBLIC_AFFILIATE_URL",
  "NEXT_PUBLIC_HUB_URL",
  "NEXT_PUBLIC_MESSENGER_URL",
  "NEXT_PUBLIC_WORKFLOW_ENGINE_URL",
  "NEXT_PUBLIC_LIVEKIT_URL",
  "NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY",
  "NEXT_PUBLIC_SENTRY_DSN",
  "NEXT_PUBLIC_LEGAL_BASE_URL",
  // OnlyOffice-Editor-Origin (drive docs/sheets/slides via WOPI): api.js + iframe.
  "NEXT_PUBLIC_ONLYOFFICE_URL",
  // Basis der oeffentlichen Buchungs-URL (janeway-calendar bookingUrl()).
  "NEXT_PUBLIC_BOOKING_BASE_URL",
  // Janeway Photo (Labs-only): Drive zeigt „Mit Janeway Photo oeffnen“ nur, wenn gesetzt.
  "NEXT_PUBLIC_PHOTO_URL",
] as const;

export type PublicEnvKey = (typeof PUBLIC_ENV_KEYS)[number];

declare global {
  interface Window {
    __JANEWAY_ENV?: Record<string, string | undefined>;
  }
}

/**
 * Liefert einen Public-Env-Wert zur Laufzeit (NICHT build-inlined).
 * Client: aus injiziertem `window.__JANEWAY_ENV`. Server: dynamisch aus process.env.
 */
export function getPublicEnv(key: PublicEnvKey): string | undefined {
  if (typeof window !== "undefined") {
    return window.__JANEWAY_ENV?.[key];
  }
  // Dynamischer Zugriff verhindert Next.js-Build-Inlining -> Laufzeit-Wert.
  return (process.env as Record<string, string | undefined>)[key];
}

/**
 * Default = der Rechtstext-Hub der Produktivumgebung (L-10), nicht die Website.
 * Bis 0.162.0 stand hier `https://janeway.io`; die Website liefert ihre Rechtstexte seit
 * 2026-08-28 nicht mehr selbst aus, sondern leitet `/agb`, `/datenschutz`, `/impressum`,
 * `/kuendigung`, `/widerruf` und `/vertraege/*` an den Hub weiter. Ein Vorgabewert, der an
 * dieser Weiterleitung haengt, ist einer zu viel — er greift ohnehin nur dort, wo die
 * Variable fehlt (PM2-Dienste, Tests): jeder Container bekommt sie aus
 * `janeway-infra/scripts/lib/common.sh` als `https://legal.${ECOSYSTEM_DOMAIN}`.
 */
const DEFAULT_LEGAL_BASE_URL = "https://legal.janeway.io";

/**
 * Zentrale URL eines Rechtsdokuments auf dem Rechtstext-Hub.
 *
 * Alle Apps verlinken hierüber auf EINE zentrale Quelle (Datenschutz, AGB,
 * Cookies, Impressum) statt eigener Volltext-Kopien. Basis kommt zur Laufzeit
 * aus `NEXT_PUBLIC_LEGAL_BASE_URL` (generisches Image): Prod = `https://legal.janeway.io`,
 * Labs = `https://legal.janewaylabs.com`. Fehlt die Env, gilt der Prod-Default.
 *
 * NUR fuer Rechtsdokumente. `kontakt` ist keines: die Kontaktseite liegt auf der
 * Website (`janeway.io/kontakt`), der Hub kennt den Pfad nicht und seine Middleware
 * schickt den Aufruf zur Anmeldung.
 *
 * @param slug z.B. "datenschutz", "agb", "cookies", "impressum", "vertraege/avv"
 */
export function centralLegalUrl(slug: string): string {
  const base = (getPublicEnv("NEXT_PUBLIC_LEGAL_BASE_URL") ?? DEFAULT_LEGAL_BASE_URL).replace(/\/+$/, "");
  const path = slug.replace(/^\/+/, "");
  return `${base}/${path}`;
}
